import { Button } from '@mantine/core';
import { Pencil } from 'lucide-react';
import { FC, useState } from 'react';

import useConfig from '@/hooks/useConfig.ts';

import { Printer } from '@/models/EnrichedPrismicDocuments.ts';
import { EternoQuoteVariant } from '@/models/EternoQuoteVariant.ts';

import { usePrinterStore } from '@/stores/printerStore.ts';

import { generatePrinterKey } from '@/utils/printUtils.ts';

import BaseModal from './BaseModal.tsx';
import EternoQuote from './EternoQuote.tsx';
import Icon from './Icon.tsx';
import RadioCard from './RadioCard.tsx';

const CustomerPrinterSelection: FC = () => {
    const [user_dismissed, setUserDismissed] = useState(false);
    const [user_opened, setUserOpened] = useState(false);
    const { selected_printer, setSelectedPrinter } = usePrinterStore();
    const { selected_location, location_key } = useConfig();

    const should_auto_open =
        !selected_printer[location_key] &&
        selected_location.configs.some((config) => config.is_new_checkin_available && config.pcs_printers?.length > 0);

    const modal_opened = user_opened || (should_auto_open && !user_dismissed);

    const printers: Printer[] = selected_location.configs.flatMap((config) => config.pcs_printers);

    const handlePrinterSelect = (field_key: string): void => {
        const printer = printers.find((printer) => generatePrinterKey(printer.id, printer.ip_address) === field_key);
        if (printer) {
            setSelectedPrinter(selected_location, printer);
        }
    };

    const handleClose = (): void => {
        setUserOpened(false);
        setUserDismissed(true);
    };

    const handleOpen = (): void => {
        setUserOpened(true);
    };

    const handleSave = (): void => {
        handleClose();
    };

    const modal_footer = (
        <div className="flex w-full justify-between">
            <Button variant="outline" onClick={handleClose}>
                Abbrechen
            </Button>
            <Button onClick={handleSave}>Speichern</Button>
        </div>
    );

    return (
        <>
            <Button
                className="border-slate-300 dark:text-neutral-300 dark:hover:text-neutral-200"
                variant="outline"
                onClick={handleOpen}
                rightSection={<Icon icon={Pencil} />}
            >
                {selected_printer[location_key] ? selected_printer[location_key]?.name : 'Select Printer'}
            </Button>
            <BaseModal
                opened={modal_opened}
                onClose={handleClose}
                title="Drucker auswählen"
                size="lg"
                footer={modal_footer}
            >
                <div className="flex flex-col gap-4 p-2">
                    {!selected_printer[location_key] && (
                        <EternoQuote
                            variant={EternoQuoteVariant.WARNING}
                            text="Es sieht so aus, als hätten Sie keinen Drucker ausgewählt."
                        />
                    )}
                    <div className="flex gap-4">
                        {printers.map((printer) => (
                            <RadioCard
                                key={generatePrinterKey(printer.id, printer.ip_address)}
                                value={generatePrinterKey(printer.id, printer.ip_address)}
                                label={printer.name}
                                checked={
                                    generatePrinterKey(
                                        selected_printer[location_key]?.id,
                                        selected_printer[location_key]?.ip_address
                                    ) === generatePrinterKey(printer.id, printer.ip_address)
                                }
                                onChange={handlePrinterSelect}
                            />
                        ))}
                    </div>
                </div>
            </BaseModal>
        </>
    );
};

export default CustomerPrinterSelection;
